// frontend/src/pages/tools/HttmSchedulerPage.tsx
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Container,
  Title,
  Text,
  Stack,
  Group,
  Button,
  Paper,
  Box,
  Table,
  Switch,
  Badge,
  Divider,
  Tooltip,
  Accordion,
  NumberInput,
  Select,
  Checkbox,
  SimpleGrid,
  SegmentedControl,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconDownload,
  IconCheck,
  IconCopy,
  IconDroplet,
  IconPill,
  IconArrowsShuffle,
  IconEye,
  IconRefresh,
  IconExternalLink,
  IconSettings,
} from '@tabler/icons-react';
import { format, parse } from 'date-fns';
import { generateHttmSchedule } from '@/utils/httmScheduleGenerator';
import { exportHttmToExcel } from '@/utils/httmExcelExport';
import { LeadGenBanner } from '@/components/public-dosing/LeadGenBanner';
import { trackEvent } from '@/utils/analytics';
import type { HttmConfig, HttmCategory } from '@/types/httmSchedule';
import {
  calculateH2o2Dose,
  calculatePraziproDose,
  AIRSTONE_COUNT,
  formatVolume,
  gallonsToLiters,
  litersToGallons,
  CHECKLIST_ITEMS,
} from '@/types/httmSchedule';

// ── URL sync helpers ──

function getConfigFromUrl(): Partial<HttmConfig> | null {
  if (typeof window === 'undefined') return null;
  const url = new URL(window.location.href);
  const s = url.searchParams.get('s');
  if (!s) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const t = url.searchParams.get('t');
  if (t && !/^\d{2}:\d{2}$/.test(t)) return null;

  const result: Partial<HttmConfig> = {
    startDate: s,
    startTime: t || '09:00',
  };

  const h = url.searchParams.get('h');
  if (h !== null) result.initialH2o2Bath = h === '1';

  const e = url.searchParams.get('e');
  if (e !== null) result.includeH2o2 = e !== '0';

  const p = url.searchParams.get('p');
  if (p !== null) result.includePrazi = p !== '0';

  const q = url.searchParams.get('q');
  if (q) {
    const parsed = parseFloat(q);
    if (!isNaN(parsed) && parsed > 0 && parsed <= 2000) result.qtTankSizeLiters = parsed;
  }

  const v = url.searchParams.get('v');
  if (v) {
    const parsed = parseFloat(v);
    if (!isNaN(parsed) && parsed > 0 && parsed <= 200) result.bathVolumeLiters = parsed;
  }

  const c = url.searchParams.get('c');
  if (c === '3' || c === '6') result.h2o2Concentration = parseInt(c) as 3 | 6;

  const u = url.searchParams.get('u');
  if (u === 'g') result.units = 'gallons';
  if (u === 'l') result.units = 'liters';

  const k = url.searchParams.get('k');
  if (k) {
    const parsed = parseInt(k);
    const maxBitmask = (1 << CHECKLIST_ITEMS.length) - 1;
    if (!isNaN(parsed) && parsed >= 0 && parsed <= maxBitmask) result.checkedItems = parsed;
  }

  return result;
}

function syncConfigToUrl(config: HttmConfig): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.set('s', config.startDate);
  url.searchParams.set('t', config.startTime);
  url.searchParams.set('h', config.initialH2o2Bath ? '1' : '0');
  url.searchParams.set('e', config.includeH2o2 ? '1' : '0');
  url.searchParams.set('p', config.includePrazi ? '1' : '0');
  url.searchParams.set('q', String(config.qtTankSizeLiters));
  url.searchParams.set('v', String(config.bathVolumeLiters));
  url.searchParams.set('c', String(config.h2o2Concentration));
  url.searchParams.set('u', config.units === 'gallons' ? 'g' : 'l');
  if (config.checkedItems > 0) {
    url.searchParams.set('k', String(config.checkedItems));
  } else {
    url.searchParams.delete('k');
  }
  window.history.replaceState({}, '', url.toString());
}

function clearUrlParams(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  ['s', 't', 'h', 'e', 'p', 'q', 'v', 'c', 'u', 'k'].forEach((p) =>
    url.searchParams.delete(p)
  );
  window.history.replaceState({}, '', url.toString());
}

// ── Category styling ──

const CATEGORY_COLORS: Record<HttmCategory, string> = {
  h2o2: 'blue',
  transfer: 'green',
  prazi: 'orange',
  observation: 'teal',
};

const CATEGORY_LABELS: Record<HttmCategory, string> = {
  h2o2: 'H2O2 Bath',
  transfer: 'Transfer',
  prazi: 'Praziquantel',
  observation: 'Observation',
};

const CATEGORY_ICONS: Record<HttmCategory, React.ReactNode> = {
  h2o2: <IconDroplet size={14} />,
  transfer: <IconArrowsShuffle size={14} />,
  prazi: <IconPill size={14} />,
  observation: <IconEye size={14} />,
};

const CATEGORY_ROW_BG: Record<HttmCategory, string> = {
  h2o2: 'var(--mantine-color-blue-0)',
  transfer: 'var(--mantine-color-green-0)',
  prazi: 'var(--mantine-color-yellow-0)',
  observation: 'var(--mantine-color-teal-0)',
};

// ── Defaults ──

const DEFAULT_QT_TANK_LITERS = 40; // ~10 gal
const DEFAULT_BATH_LITERS = 6;
const DEFAULT_H2O2_CONCENTRATION: 3 | 6 = 3;

// ── Component ──

export function HttmSchedulerPage() {
  const today = format(new Date(), 'yyyy-MM-dd');
  const urlConfig = useRef(getConfigFromUrl());

  const [startDate, setStartDate] = useState<string>(
    urlConfig.current?.startDate || today
  );
  const [startTime, setStartTime] = useState<string>(
    urlConfig.current?.startTime || '09:00'
  );
  const [initialH2o2Bath, setInitialH2o2Bath] = useState<boolean>(
    urlConfig.current?.initialH2o2Bath ?? true
  );
  const [includeH2o2, setIncludeH2o2] = useState<boolean>(
    urlConfig.current?.includeH2o2 ?? true
  );
  const [includePrazi, setIncludePrazi] = useState<boolean>(
    urlConfig.current?.includePrazi ?? true
  );
  const [qtTankSizeLiters, setQtTankSizeLiters] = useState<number>(
    urlConfig.current?.qtTankSizeLiters ?? DEFAULT_QT_TANK_LITERS
  );
  const [bathVolumeLiters, setBathVolumeLiters] = useState<number>(
    urlConfig.current?.bathVolumeLiters ?? DEFAULT_BATH_LITERS
  );
  const [h2o2Concentration, setH2o2Concentration] = useState<3 | 6>(
    urlConfig.current?.h2o2Concentration ?? DEFAULT_H2O2_CONCENTRATION
  );
  const [units, setUnits] = useState<'liters' | 'gallons'>(
    urlConfig.current?.units ?? 'liters'
  );
  const [checkedItems, setCheckedItems] = useState<number>(
    urlConfig.current?.checkedItems ?? 0
  );
  const [copied, setCopied] = useState(false);
  const [excelLoading, setExcelLoading] = useState(false);
  const [excelError, setExcelError] = useState<string | null>(null);

  const config: HttmConfig = useMemo(
    () => ({
      startDate,
      startTime,
      initialH2o2Bath,
      includeH2o2,
      includePrazi,
      qtTankSizeLiters,
      bathVolumeLiters,
      h2o2Concentration,
      units,
      checkedItems,
    }),
    [
      startDate,
      startTime,
      initialH2o2Bath,
      includeH2o2,
      includePrazi,
      qtTankSizeLiters,
      bathVolumeLiters,
      h2o2Concentration,
      units,
      checkedItems,
    ]
  );

  const schedule = useMemo(() => generateHttmSchedule(config), [config]);

  // Dose calculations for display
  const h2o2DosePerBath = useMemo(
    () => calculateH2o2Dose(bathVolumeLiters, h2o2Concentration),
    [bathVolumeLiters, h2o2Concentration]
  );
  const praziDosePerTreatment = useMemo(
    () => calculatePraziproDose(qtTankSizeLiters),
    [qtTankSizeLiters]
  );
  const airstoneCount = AIRSTONE_COUNT;

  // Sync config to URL whenever it changes
  useEffect(() => {
    syncConfigToUrl(config);
  }, [config]);

  // SEO meta tags
  useEffect(() => {
    const pageTitle = 'Free HTTM Quarantine Scheduler | Reef Data Hub';
    const pageDescription =
      'Plan your Hybrid Tank Transfer Method quarantine schedule. Set your start date, get a full day-by-day protocol with H2O2 baths, Prazi dosing, and tank transfers. Includes dose calculators and equipment checklist. Export to Excel.';

    const originalTitle = document.title;
    document.title = pageTitle;

    const createdTags: HTMLMetaElement[] = [];

    let metaDesc = document.querySelector(
      'meta[name="description"]'
    ) as HTMLMetaElement | null;
    const originalMetaDescContent = metaDesc?.getAttribute('content') ?? null;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
      createdTags.push(metaDesc);
    }
    metaDesc.setAttribute('content', pageDescription);

    const sanitizeUrl = (url: string): string => {
      try {
        const parsed = new URL(url);
        if (!['http:', 'https:'].includes(parsed.protocol)) {
          return window.location.origin + '/tools/httm-scheduler';
        }
        return parsed.origin + parsed.pathname;
      } catch {
        return window.location.origin + '/tools/httm-scheduler';
      }
    };

    const ogTags: Array<{ property: string; content: string }> = [
      { property: 'og:title', content: pageTitle },
      { property: 'og:description', content: pageDescription },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: sanitizeUrl(window.location.href) },
    ];

    const originalOgValues: Map<string, string | null> = new Map();

    ogTags.forEach(({ property, content }) => {
      let meta = document.querySelector(
        `meta[property="${property}"]`
      ) as HTMLMetaElement | null;
      originalOgValues.set(property, meta?.getAttribute('content') ?? null);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('property', property);
        document.head.appendChild(meta);
        createdTags.push(meta);
      }
      meta.setAttribute('content', content);
    });

    trackEvent('page_view', {
      page_title: 'HTTM Quarantine Scheduler',
      page_location: window.location.href,
      page_path: '/tools/httm-scheduler',
    });

    return () => {
      document.title = originalTitle;
      createdTags.forEach((tag) => {
        if (tag.parentNode) tag.parentNode.removeChild(tag);
      });
      if (originalMetaDescContent !== null) {
        const desc = document.querySelector('meta[name="description"]');
        desc?.setAttribute('content', originalMetaDescContent);
      }
      originalOgValues.forEach((originalValue, property) => {
        if (originalValue !== null) {
          const meta = document.querySelector(
            `meta[property="${property}"]`
          );
          meta?.setAttribute('content', originalValue);
        }
      });
    };
  }, []);

  // ── Handlers ──

  const handleCopyLink = useCallback(async () => {
    syncConfigToUrl(config);
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      trackEvent('share', {
        method: 'copy_link',
        content_type: 'httm_schedule',
        item_id: config.startDate,
      });
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [config]);

  const handleExportExcel = useCallback(async () => {
    setExcelError(null);
    setExcelLoading(true);
    try {
      await exportHttmToExcel(schedule, config);
    } catch (error) {
      console.error('Excel export failed:', error);
      setExcelError('Failed to export Excel file. Please try again.');
    } finally {
      setExcelLoading(false);
    }
  }, [schedule, config]);

  const handleReset = useCallback(() => {
    const resetDate = format(new Date(), 'yyyy-MM-dd');
    setStartDate(resetDate);
    setStartTime('09:00');
    setInitialH2o2Bath(true);
    setIncludeH2o2(true);
    setIncludePrazi(true);
    setQtTankSizeLiters(DEFAULT_QT_TANK_LITERS);
    setBathVolumeLiters(DEFAULT_BATH_LITERS);
    setH2o2Concentration(DEFAULT_H2O2_CONCENTRATION);
    setUnits('liters');
    setCheckedItems(0);
    clearUrlParams();
    trackEvent('select_content', {
      content_type: 'action',
      item_id: 'reset_httm_schedule',
    });
  }, []);

  const toggleChecklistItem = useCallback((index: number) => {
    setCheckedItems((prev) => prev ^ (1 << index));
  }, []);

  // Parse startDate string into a Date object for the DatePickerInput
  const dateValue = useMemo(() => {
    try {
      return parse(startDate, 'yyyy-MM-dd', new Date());
    } catch {
      return new Date();
    }
  }, [startDate]);

  // Volume display helpers - convert liters to display unit for inputs
  const tankSizeDisplay = units === 'gallons' ? litersToGallons(qtTankSizeLiters) : qtTankSizeLiters;
  const bathVolumeDisplay = units === 'gallons' ? litersToGallons(bathVolumeLiters) : bathVolumeLiters;
  const volumeUnit = units === 'gallons' ? 'gal' : 'L';

  const handleTankSizeChange = useCallback(
    (val: string | number) => {
      const num = typeof val === 'string' ? parseFloat(val) : val;
      if (isNaN(num) || num <= 0) return;
      setQtTankSizeLiters(
        units === 'gallons' ? gallonsToLiters(num) : num
      );
    },
    [units]
  );

  const handleBathVolumeChange = useCallback(
    (val: string | number) => {
      const num = typeof val === 'string' ? parseFloat(val) : val;
      if (isNaN(num) || num <= 0) return;
      setBathVolumeLiters(
        units === 'gallons' ? gallonsToLiters(num) : num
      );
    },
    [units]
  );

  // Group checklist items by group for display
  const checklistGroups = useMemo(() => {
    const groups: Record<string, { item: (typeof CHECKLIST_ITEMS)[number]; index: number }[]> = {};
    CHECKLIST_ITEMS.forEach((item, index) => {
      if (!groups[item.group]) groups[item.group] = [];
      groups[item.group]!.push({ item, index });
    });
    return groups;
  }, []);

  const checkedCount = useMemo(() => {
    let count = 0;
    for (let i = 0; i < CHECKLIST_ITEMS.length; i++) {
      if (checkedItems & (1 << i)) count++;
    }
    return count;
  }, [checkedItems]);

  return (
      <Container size="lg" py="xl">
        <Stack gap="lg">
          {/* Page Header */}
          <div>
            <Title order={1}>HTTM Quarantine Scheduler</Title>
            <Text c="dimmed" mt="xs">
              Plan your Hybrid Tank Transfer Method quarantine schedule.
              Adjust your setup below, then follow the day-by-day protocol.
              No signup required.
            </Text>
          </div>

          {/* Config Accordion - open by default */}
          <Accordion variant="contained" radius="md" defaultValue="setup">
            <Accordion.Item value="setup">
              <Accordion.Control>
                <Group gap="xs">
                  <IconSettings size={18} />
                  <Text fw={500}>Setup</Text>
                  <Text size="xs" c="dimmed">
                    &mdash; {formatVolume(qtTankSizeLiters, units)} QT
                    tank
                    {includeH2o2 && <>, {formatVolume(bathVolumeLiters, units)} bath, {h2o2Concentration}% H2O2{initialH2o2Bath ? ', Day 1 bath' : ', no Day 1 bath'}</>}
                    {!includeH2o2 && ', no H2O2'}
                    {!includePrazi && ', no Prazi'}
                  </Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <Group justify="flex-end">
                    <SegmentedControl
                      size="xs"
                      value={units}
                      onChange={(val) => setUnits(val as 'liters' | 'gallons')}
                      data={[
                        { label: 'Litres', value: 'liters' },
                        { label: 'Gallons', value: 'gallons' },
                      ]}
                    />
                  </Group>

                  <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                    <DatePickerInput
                      label="Start Date"
                      value={dateValue}
                      onChange={(val) => {
                        if (val) setStartDate(format(val, 'yyyy-MM-dd'));
                      }}
                      aria-label="Quarantine start date"
                    />
                    <Box>
                      <Text size="sm" fw={500} mb={4}>
                        Start Time
                      </Text>
                      <input
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        aria-label="Quarantine start time"
                        style={{
                          height: 36,
                          width: '100%',
                          padding: '0 12px',
                          border: '1px solid var(--mantine-color-default-border)',
                          borderRadius: 'var(--mantine-radius-sm)',
                          backgroundColor: 'var(--mantine-color-white)',
                          fontSize: 'var(--mantine-font-size-sm)',
                          color: 'var(--mantine-color-text)',
                        }}
                      />
                    </Box>
                    <NumberInput
                      label={`QT Tank Size (${volumeUnit})`}
                      value={Math.round(tankSizeDisplay * 10) / 10}
                      onChange={handleTankSizeChange}
                      min={1}
                      max={units === 'gallons' ? 500 : 2000}
                      step={units === 'gallons' ? 1 : 5}
                      decimalScale={1}
                      aria-label="Quarantine tank size"
                    />
                    {includeH2o2 && (
                    <NumberInput
                      label={`H2O2 Bath Volume (${volumeUnit})`}
                      value={Math.round(bathVolumeDisplay * 10) / 10}
                      onChange={handleBathVolumeChange}
                      min={1}
                      max={units === 'gallons' ? 50 : 200}
                      step={units === 'gallons' ? 0.5 : 1}
                      decimalScale={1}
                      aria-label="H2O2 bath container volume"
                    />
                    )}
                    {includeH2o2 && (
                    <Select
                      label="H2O2 Concentration"
                      value={String(h2o2Concentration)}
                      onChange={(val) => {
                        if (val === '3' || val === '6')
                          setH2o2Concentration(parseInt(val) as 3 | 6);
                      }}
                      data={[
                        { value: '3', label: '3% (standard drugstore)' },
                        { value: '6', label: '6% (salon / food grade)' },
                      ]}
                      aria-label="Hydrogen peroxide concentration"
                    />
                    )}
                    <Switch
                      label="H2O2 bath at start (Day 1)"
                      checked={initialH2o2Bath}
                      onChange={(e) =>
                        setInitialH2o2Bath(e.currentTarget.checked)
                      }
                      description="Skips Day 13 H2O2 if done at start"
                      mt="xs"
                      disabled={!includeH2o2}
                    />
                    <Switch
                      label="Include H2O2 Baths"
                      checked={includeH2o2}
                      onChange={(e) =>
                        setIncludeH2o2(e.currentTarget.checked)
                      }
                      description="Hydrogen peroxide dips between transfers"
                      mt="xs"
                    />
                    <Switch
                      label="Include PraziPro"
                      checked={includePrazi}
                      onChange={(e) =>
                        setIncludePrazi(e.currentTarget.checked)
                      }
                      description="Praziquantel dosing for flukes"
                      mt="xs"
                    />
                  </SimpleGrid>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>

          {/* Compact dose summary bar */}
          {(includeH2o2 || includePrazi) && (
          <Paper p="xs" px="md" radius="md" withBorder>
            <Group justify="space-between" wrap="wrap" gap="xs">
              <Group gap="lg" wrap="wrap">
                {includeH2o2 && (
                <Group gap={6}>
                  <IconDroplet size={14} color="var(--mantine-color-blue-6)" />
                  <Text size="sm" c="dimmed">H2O2:</Text>
                  <Text size="sm" fw={600}>
                    {h2o2DosePerBath.toFixed(1)} ml of {h2o2Concentration}%
                  </Text>
                  <Text size="xs" c="dimmed">
                    per bath into {formatVolume(bathVolumeLiters, units)}
                  </Text>
                </Group>
                )}
                {includePrazi && (
                <Group gap={6}>
                  <IconPill size={14} color="var(--mantine-color-orange-6)" />
                  <Text size="sm" c="dimmed">PraziPro:</Text>
                  <Text size="sm" fw={600}>
                    {praziDosePerTreatment.toFixed(1)} ml
                  </Text>
                  <Text size="xs" c="dimmed">
                    per dose for {formatVolume(qtTankSizeLiters, units)}
                  </Text>
                </Group>
                )}
              </Group>
              <Group gap={4}>
                {includeH2o2 && (
                <Badge variant="light" color="blue" size="sm">
                  {(h2o2DosePerBath * 2).toFixed(0)} ml H2O2 total
                </Badge>
                )}
                {includePrazi && (
                <Badge variant="light" color="orange" size="sm">
                  {(praziDosePerTreatment * 2).toFixed(0)} ml Prazi total
                </Badge>
                )}
              </Group>
            </Group>
          </Paper>
          )}

          {/* Schedule Table - the hero */}
          <Paper p="md" radius="md" withBorder>
            <Group justify="space-between" mb="md" wrap="wrap">
              <Title order={3}>
                Your Quarantine Schedule
              </Title>
              <Group gap="sm" wrap="wrap">
                <Button
                  variant="subtle"
                  color="gray"
                  onClick={handleReset}
                  size="xs"
                  leftSection={<IconRefresh size={14} />}
                >
                  Reset
                </Button>
                <Button
                  leftSection={
                    copied ? <IconCheck size={14} /> : <IconCopy size={14} />
                  }
                  variant="light"
                  onClick={handleCopyLink}
                  color={copied ? 'teal' : undefined}
                  size="xs"
                >
                  {copied ? 'Copied!' : 'Copy Link'}
                </Button>
                <Button
                  leftSection={<IconDownload size={14} />}
                  onClick={handleExportExcel}
                  loading={excelLoading}
                  size="xs"
                >
                  Download Excel
                </Button>
              </Group>
            </Group>
            {excelError && (
              <Text c="red" size="sm" mb="sm">
                {excelError}
              </Text>
            )}
            <Table.ScrollContainer minWidth={700}>
              <Table striped={false} highlightOnHover withTableBorder>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 55 }}>Day</Table.Th>
                    <Table.Th style={{ width: 140 }}>Date</Table.Th>
                    <Table.Th style={{ width: 85 }}>By</Table.Th>
                    <Table.Th>Action</Table.Th>
                    <Table.Th style={{ width: 120 }}>Type</Table.Th>
                    <Table.Th style={{ width: 100 }}>Location</Table.Th>
                    <Table.Th>Notes</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {schedule.map((event, i) => (
                    <Table.Tr
                      key={i}
                      style={{
                        backgroundColor: event.skipped
                          ? 'var(--mantine-color-gray-1)'
                          : CATEGORY_ROW_BG[event.category],
                        opacity: event.skipped ? 0.6 : 1,
                      }}
                    >
                      <Table.Td fw={600}>{event.day}</Table.Td>
                      <Table.Td>
                        {format(event.date, 'EEE, MMM d')}
                      </Table.Td>
                      <Table.Td>{format(event.date, 'h:mm a')}</Table.Td>
                      <Table.Td>
                        <Text
                          size="sm"
                          fw={500}
                          td={event.skipped ? 'line-through' : undefined}
                        >
                          {event.action}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {event.timing}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Tooltip label={CATEGORY_LABELS[event.category]}>
                          <Badge
                            color={CATEGORY_COLORS[event.category]}
                            variant="light"
                            size="sm"
                            leftSection={CATEGORY_ICONS[event.category]}
                          >
                            {CATEGORY_LABELS[event.category]}
                          </Badge>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{event.tank}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text
                          size="xs"
                          c={event.skipped ? 'red' : 'dimmed'}
                          td={event.skipped ? 'line-through' : undefined}
                        >
                          {event.notes}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Paper>

          {/* Lead Gen Banner - below schedule */}
          <LeadGenBanner />

          {/* Collapsible sections below the schedule */}
          <Accordion variant="contained" radius="md" multiple>
            {/* Equipment Checklist - closed by default */}
            <Accordion.Item value="checklist">
              <Accordion.Control>
                <Group gap="xs">
                  <Text fw={500}>Equipment Checklist</Text>
                  <Badge variant="light" size="sm">
                    {checkedCount} / {CHECKLIST_ITEMS.length}
                  </Badge>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  {Object.entries(checklistGroups).map(
                    ([group, items]) => (
                      <div key={group}>
                        <Text size="sm" fw={600} c="dimmed" mb="xs">
                          {group}
                        </Text>
                        <Stack gap={6}>
                          {items.map(({ item, index }) => {
                            // Build dynamic label with quantities
                            let label = item.label;
                            if (item.id === 'airstones')
                              label += ` - need at least ${airstoneCount}`;
                            if (item.id === 'h2o2')
                              label += ` (${h2o2Concentration}%) - need ${(h2o2DosePerBath * 2).toFixed(0)} ml total (based on tank size & concentration above)`;
                            if (item.id === 'prazipro')
                              label += ` - need ${(praziDosePerTreatment * 2).toFixed(1)} ml total (based on tank size above)`;
                            if (item.id === 'bath-container')
                              label += ` (${formatVolume(bathVolumeLiters, units)})`;

                            return (
                              <Checkbox
                                key={item.id}
                                label={label}
                                checked={
                                  (checkedItems & (1 << index)) !== 0
                                }
                                onChange={() =>
                                  toggleChecklistItem(index)
                                }
                                size="sm"
                              />
                            );
                          })}
                        </Stack>
                      </div>
                    )
                  )}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            {/* Dose Details - closed by default */}
            <Accordion.Item value="doses">
              <Accordion.Control>
                <Text fw={500}>Dose Calculators (Detail)</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                  <Paper p="md" radius="md" withBorder>
                    <Group gap="xs" mb="xs">
                      <IconDroplet size={18} color="var(--mantine-color-blue-6)" />
                      <Text fw={600} size="sm">
                        H2O2 Dosing
                      </Text>
                    </Group>
                    <Stack gap={4}>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Per bath:
                        </Text>
                        <Text size="sm" fw={600}>
                          {h2o2DosePerBath.toFixed(1)} ml of{' '}
                          {h2o2Concentration}%
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Into:
                        </Text>
                        <Text size="sm">
                          {formatVolume(bathVolumeLiters, units)} saltwater
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Duration:
                        </Text>
                        <Text size="sm">30 minutes with aeration</Text>
                      </Group>
                      <Divider my={4} />
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Total needed (2 baths):
                        </Text>
                        <Text size="sm" fw={700} c="blue">
                          {(h2o2DosePerBath * 2).toFixed(1)} ml
                        </Text>
                      </Group>
                    </Stack>
                  </Paper>

                  <Paper p="md" radius="md" withBorder>
                    <Group gap="xs" mb="xs">
                      <IconPill size={18} color="var(--mantine-color-orange-6)" />
                      <Text fw={600} size="sm">
                        PraziPro Dosing
                      </Text>
                    </Group>
                    <Stack gap={4}>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Per dose:
                        </Text>
                        <Text size="sm" fw={600}>
                          {praziDosePerTreatment.toFixed(1)} ml
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          For tank:
                        </Text>
                        <Text size="sm">
                          {formatVolume(qtTankSizeLiters, units)}
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Formula:
                        </Text>
                        <Text size="sm">1 tsp (5 ml) per 20 US gal</Text>
                      </Group>
                      <Divider my={4} />
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Total needed (2 doses):
                        </Text>
                        <Text size="sm" fw={700} c="orange">
                          {(praziDosePerTreatment * 2).toFixed(1)} ml
                        </Text>
                      </Group>
                    </Stack>
                  </Paper>
                </SimpleGrid>
              </Accordion.Panel>
            </Accordion.Item>

            {/* About Protocol - closed by default */}
            <Accordion.Item value="about">
              <Accordion.Control>
                <Text fw={500}>About the HTTM Protocol</Text>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="sm">
                  <Text size="sm">
                    The{' '}
                    <strong>
                      HTTM (Hybrid Tank Transfer Method)
                    </strong>{' '}
                    is a quarantine protocol for marine fish that combines the
                    Tank Transfer Method with Hydrogen Peroxide (H2O2) baths
                    and Praziquantel dosing. It treats Marine Ich (Cryptocaryon
                    irritans) and flukes without using copper.
                  </Text>
                  <Text size="sm" fw={600}>
                    How it works:
                  </Text>
                  <Text size="sm">
                    Fish are transferred between two tanks every 72 hours.
                    This breaks the Ich lifecycle because the parasites fall
                    off the fish and into the tank, and the fish is moved to a
                    clean tank before the parasites can reattach. The vacated
                    tank is cleaned and refilled, eliminating the parasites.
                  </Text>
                  <Text size="sm" fw={600}>
                    H2O2 Bath Instructions:
                  </Text>
                  <Text size="sm">
                    Add {h2o2DosePerBath.toFixed(1)} ml of{' '}
                    {h2o2Concentration}% hydrogen peroxide into{' '}
                    {formatVolume(bathVolumeLiters, units)} of saltwater
                    (matched to tank salinity and temperature). Place the fish
                    in the bath for 30 minutes with an airline for aeration.
                    This dip treats external parasites and bacterial/fungal
                    issues.
                  </Text>
                  <Text size="sm" fw={600}>
                    Important reminders:
                  </Text>
                  <Text size="sm" component="div">
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      <li>
                        Transfers must happen BEFORE 72 hours to break the
                        Ich lifecycle
                      </li>
                      <li>
                        Match salinity and temperature between tanks before
                        transferring
                      </li>
                      <li>
                        Clean tanks with vinegar or bleach between uses,
                        rinse thoroughly
                      </li>
                      <li>
                        Discard airstones after each use - do not reuse
                      </li>
                      <li>
                        Monitor ammonia with badges - QT tanks have no
                        biological filtration
                      </li>
                      <li>
                        No dechlorinator needed - saltwater is mixed fresh
                      </li>
                    </ul>
                  </Text>
                  <Divider my="sm" />
                  <Text size="sm" fw={600}>
                    Reference Links:
                  </Text>
                  <Text size="sm" component="div">
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      <li>
                        <a
                          href="https://humble.fish/community/threads/hybrid-ttm-to-treat-more-parasites.1765/"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'var(--mantine-color-teal-6)' }}
                        >
                          HTTM Method in Detail
                          <IconExternalLink size={12} style={{ marginLeft: 4, verticalAlign: 'middle' }} />
                        </a>
                      </li>
                      <li>
                        <a
                          href="https://humble.fish/community/threads/hydrogen-peroxide-bath.1767/"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'var(--mantine-color-teal-6)' }}
                        >
                          H2O2 Bath Instructions
                          <IconExternalLink size={12} style={{ marginLeft: 4, verticalAlign: 'middle' }} />
                        </a>
                      </li>
                      <li>
                        <a
                          href="https://humble.fish/community/threads/fish-peroxide-compatibility-list.1789/"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: 'var(--mantine-color-teal-6)' }}
                        >
                          H2O2 Fish Compatibility List
                          <IconExternalLink size={12} style={{ marginLeft: 4, verticalAlign: 'middle' }} />
                        </a>
                      </li>
                    </ul>
                  </Text>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>

          {/* Footer */}
          <Text size="xs" c="dimmed" ta="center">
            This scheduler is provided free by Reef Data Hub. Your settings
            and checklist progress are encoded in the URL for easy sharing and
            bookmarking.
          </Text>
          <Text size="sm" c="dimmed" ta="center">
            Reef Data Hub is a modern tool to integrate, track, and analyze
            data from all sources connected to your reef tank.
          </Text>
        </Stack>
      </Container>
  );
}
